// src/whatsappChat/pages/InboxPage.jsx
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faComments } from '@fortawesome/free-regular-svg-icons';
import { useWaConversations, useWaNumbers } from '../api/hooks';
import { useWaSocket } from '../socket/WaSocketProvider';
import { useWaChatContext } from '../components/WaChatLayout';
import { ConversationList } from '../components/ConversationList';
import { ThreadView } from '../components/ThreadView';
import { NewChatModal } from '../components/NewChatModal';
import '../styles/inbox.css';
import '../styles/compose.css';

const SEARCH_DEBOUNCE_MS = 350;

export default function InboxPage() {
  const { permissions } = useWaChatContext();
  const qc = useQueryClient();
  const { connected } = useWaSocket();
  const [searchParams, setSearchParams] = useSearchParams();

  // The open chat lives in the URL (?chat=42) so it can be linked to.
  const activeId = Number(searchParams.get('chat')) || null;

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [numberId, setNumberId] = useState('');
  const [newChat, setNewChat] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchInput.trim()), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data: numbers = [], isSuccess: numbersLoaded } = useWaNumbers();
  const query = useWaConversations({ search, filter, numberId });

  const selectChat = (id) => {
    setSearchParams(id ? { chat: String(id) } : {}, { replace: !id });
  };

  const openStartedChat = (id) => {
    setNewChat(false);
    selectChat(id);
    // The socket pushes the new row; without it, reload the list.
    if (!connected) qc.invalidateQueries({ queryKey: ['wa-conversations'] });
  };

  const listRow = query.conversations.find((row) => row.id === activeId) || null;
  const canSend = Boolean(permissions.inbox.send);

  return (
    <div className={`wa-inbox${activeId ? ' has-active' : ''}`}>
      <ConversationList
        query={query}
        activeId={activeId}
        onSelect={selectChat}
        search={searchInput}
        onSearch={setSearchInput}
        filter={filter}
        onFilter={setFilter}
        numbers={numbers}
        numberId={numberId}
        onNumber={setNumberId}
        showUnknown={permissions.inbox.unknown_numbers}
        onNewChat={canSend ? () => setNewChat(true) : null}
      />

      {activeId ? (
        <ThreadView
          key={activeId}
          conversationId={activeId}
          listRow={listRow}
          numbers={numbers}
          numbersLoaded={numbersLoaded}
          canSend={canSend}
          onBack={() => selectChat(null)}
        />
      ) : (
        <section className="wa-thread wa-thread-empty">
          <div className="wa-state">
            <FontAwesomeIcon icon={faComments} className="wa-empty-icon" />
            <p>Select a chat to read the conversation.</p>
          </div>
        </section>
      )}

      {newChat && (
        <NewChatModal numbers={numbers} onClose={() => setNewChat(false)} onOpenChat={openStartedChat} />
      )}
    </div>
  );
}
